import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, Drawer, FormControlLabel, FormGroup, Select, MenuItem, TextField, Typography, IconButton, Tooltip, Snackbar } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { TrimbleColors } from 'legacy-components/TrimbleColors';
import { resolveTextToFilter } from './filter';
import EventService from '../service/EventService';
import { useValidateFilterCondition } from './EventFilterValidation';
import { useIdentityEnvironmentFilter } from 'pages/home/subscriptions/components/identity-environment-filter/IdentityEnvironmentFilter';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles({
    customTooltip: {
      fontSize: '16px',
      lineHeight: 1.7,
    },
    alert: {
        fontSize: '18px'
    },
    alertIcon: {
        fontSize: '25px'
    }
  });

const StyledSelect = withStyles({
    root: {
      fontFamily: 'sans-serif',
      fontSize: '18px',
      fontWeight: 300,
    },
    icon: {
      color: TrimbleColors.Blue,
    },
  })(Select);

  const StyledMenuItem = withStyles({
    root: {
      fontFamily: 'sans-serif',
      fontSize: '18px',
      fontWeight: 300,
    },
  })(MenuItem);


interface EventDrawerProps {
    open: boolean;
    onClose: () => void;
    serviceList?: { [key: string]: string[] };
    subscriptionId: string;
    applicationId: string| undefined;
    onChange: () => void;
    regionMap?: { [version: string]: { [environment: string]: { available: string[], subscribed: string[] } } };
}

const EventDrawer: React.FC<EventDrawerProps> = ({ open, onClose, serviceList, subscriptionId, applicationId, onChange, regionMap }) => {
    const classes = useStyles();
    const [selectedServiceInDrawer, setSelectedServiceInDrawer] = useState<string>('');
    const [selectedDeploymentInDrawer, setSelectedDeploymentInDrawer] = useState<string>('');
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [description, setDescription] = useState<string>('');
    const [filter, setFilter] = useState<string>('');
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const { validateFilterCondition } = useValidateFilterCondition();
    const [isFilterValid, setIsFilterValid] = useState<boolean>(true);

     const {eventConfiglist } = useIdentityEnvironmentFilter();

     const [serviceDeploymentList, setServiceDeploymentList] = useState<string[]>(eventConfiglist);

     const [alertVisible, setAlertVisible] = useState<boolean>(false);
     const [alertMessage, setAlertMessage] = useState<string>('');

    const availableRegions: string[] = selectedServiceInDrawer && selectedDeploymentInDrawer
        ? regionMap?.[selectedServiceInDrawer]?.[selectedDeploymentInDrawer]?.available ?? []
        : [];
    const subscribedRegions: string[] = selectedServiceInDrawer && selectedDeploymentInDrawer
        ? regionMap?.[selectedServiceInDrawer]?.[selectedDeploymentInDrawer]?.subscribed ?? []
        : [];
    // Show the region section whenever at least one region exists.
    // isMultiRegion drives interactive vs. read-only behaviour.
    const showRegionSelector = (availableRegions.length + subscribedRegions.length) > 0;
    const isMultiRegion = (availableRegions.length + subscribedRegions.length) > 1;

    // Auto-select the single available region so the form stays valid without user action.
    useEffect(() => {
        if (!isMultiRegion && availableRegions.length === 1) {
            setSelectedRegions([availableRegions[0]]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableRegions.join(','), isMultiRegion]);

    useEffect(() => {
        const regionValid = !showRegionSelector || selectedRegions.length > 0;
        setIsFormValid(
            selectedServiceInDrawer !== '' &&
            selectedDeploymentInDrawer !== '' &&
            description.trim() !== '' &&
            isFilterValid === true &&
            regionValid
        );
    }, [selectedServiceInDrawer, selectedDeploymentInDrawer, description, filter, isFilterValid, selectedRegions, showRegionSelector]);

    const handleServiceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const selectedService = event.target.value as string;
        setSelectedServiceInDrawer(selectedService);
        if (serviceList!==undefined)
            setServiceDeploymentList(serviceList[selectedService]);
        setSelectedDeploymentInDrawer('');
        setSelectedRegions([]);
    };

    const handleDeploymentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setSelectedDeploymentInDrawer(event.target.value as string);
        setSelectedRegions([]);
    };

    const handleRegionToggle = (region: string) => {
        setSelectedRegions(prev =>
            prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]
        );
    };

    const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        if (name === 'description') {
            setDescription(value);
        }
        if (name === 'filter'){
            setFilter(value);
            if(value===''){
                setIsFilterValid(true);
            }else{
            setIsFilterValid(false);
            }
        }
    };

    const handleFormSubmit =  async (event: React.FormEvent) => {
        event.preventDefault();
        if (isFormValid) {
            const basePayload = {
                description: description,
                environment: selectedDeploymentInDrawer,
                applicationId: applicationId,
                service_version: selectedServiceInDrawer,
                filter: filter !== '' ? resolveTextToFilter(filter) : null,
            };

            if (showRegionSelector && selectedRegions.length > 0) {
                // Sequential creation per region (production multi-region)
                const errors: string[] = [];
                let anySuccess = false;
                for (const region of selectedRegions) {
                    try {
                        const response = await EventService.createEventSubscription(
                            { ...basePayload, region },
                            subscriptionId
                        ).toPromise();
                        if (response.status === 201) {
                            anySuccess = true;
                        } else {
                            errors.push(`Region "${region}": ${response.status} - ${response.statusText}`);
                        }
                    } catch (error) {
                        const err = error as any;
                        const detail = err?.response?.data?.message || err?.message || 'Unknown error';
                        errors.push(`Region "${region}": ${detail}`);
                    }
                }
                if (anySuccess) {
                    onChange();
                }
                if (errors.length > 0) {
                    setAlertMessage(`Failed to create subscription for:\n${errors.join('\n')}`);
                    setAlertVisible(true);
                    if (!anySuccess) {
                        resetForm();
                        return;
                    }
                }
                if (errors.length === 0) {
                    resetForm();
                    onClose();
                } else {
                    resetForm();
                }
            } else {
                // Single create: auto-include region when only one is available (e.g. "us" default),
                // or omit region for older API responses without region data.
                const autoRegion = availableRegions.length > 0 ? availableRegions[0] : undefined;
                const payload = autoRegion ? { ...basePayload, region: autoRegion } : basePayload;
                try {
                    const response = await EventService.createEventSubscription(payload, subscriptionId).toPromise();
                    if (response.status === 201) {
                        onChange();
                        resetForm();
                        onClose();
                    } else {
                        setAlertMessage(`Error: ${response.status} - ${response.statusText}`);
                        setAlertVisible(true);
                        resetForm();
                    }
                } catch (error) {
                    console.error("Error while creating event configuration:", error);
                    setAlertMessage(`Error while creating event configuration`);
                    setAlertVisible(true);
                    resetForm();
                }
            }
        }
    };

    const resetForm = () => {
        setServiceDeploymentList([]);
        setSelectedServiceInDrawer('');
        setSelectedDeploymentInDrawer('');
        setSelectedRegions([]);
        setFilter('');
        setDescription('');
    };

    const handleVerifyClick = () => {
        const validationResult = validateFilterCondition(filter);
        setIsFilterValid(validationResult);
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} style={{ width: 950 }}>
            <Snackbar
                open={alertVisible}
                autoHideDuration={6000}
                onClose={() => setAlertVisible(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="error" onClose={() => setAlertVisible(false)} classes={{ message: classes.alert, icon: classes.alertIcon }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
            <Box p={2} style={{ width: 933, paddingLeft: 54, maxHeight: '100vh', overflowY: 'auto', fontFamily: 'sans-serif' }}>
                <Typography style={{ marginBottom: 16, marginLeft:1, fontSize: '24px' }}>
                    New Event Configuration
                </Typography>
                <Typography variant="body1" style={{ fontSize: '16px', fontWeight: 300, marginBottom: 19, lineHeight: '20px', textAlign: 'left', letterSpacing: '0.15000000596046448px' }}>
                    Create a new event specific to your environment and version
                </Typography>
                <form onSubmit={handleFormSubmit}>
                    <Box mb={2}>
                        <Typography style={{ marginBottom: 8, fontSize: '19px', fontWeight: 400, marginTop: 40 }}>
                            Versions
                        </Typography>
                        <StyledSelect
                            fullWidth
                            variant="outlined"
                            value={selectedServiceInDrawer}
                            onChange={handleServiceChange}
                            displayEmpty
                            style={{width: 800}}
                            IconComponent={ExpandMoreIcon}
                        >
                            <StyledMenuItem value="" disabled>
                                <Typography style={{fontSize: '18px', fontWeight: 300, color: '#999' }}>Select a Version</Typography>
                            </StyledMenuItem>
                            {serviceList && Object.entries(serviceList).map(([version, deployments], index) => (
                                <StyledMenuItem key={index} value={version}>
                                    {version}
                                </StyledMenuItem>
                            ))}
                        </StyledSelect>
                        <Typography style={{ marginBottom: 8, marginTop: 50, fontSize: '19px', fontWeight: 400 }}>
                            Deployment
                        </Typography>
                        <StyledSelect
                            fullWidth
                            variant="outlined"
                            value={selectedDeploymentInDrawer}
                            onChange={handleDeploymentChange}
                            displayEmpty
                            style={{width: 800}}
                            IconComponent={ExpandMoreIcon}
                        >
                            <StyledMenuItem value="" disabled>
                                <Typography style={{ fontSize: '18px', fontWeight: 300, color: '#999' }}>Select a Deployment</Typography>
                            </StyledMenuItem>
                            {serviceDeploymentList.map((deployment, index) => (
                                <StyledMenuItem key={index} value={deployment}>
                                    {deployment}
                                </StyledMenuItem>
                            ))}
                        </StyledSelect>

                        {showRegionSelector && (
                            <>
                                <Typography style={{ marginBottom: 8, marginTop: 50, fontSize: '19px', fontWeight: 400 }}>
                                    Region
                                </Typography>
                                <FormGroup style={{ width: 800 }}>
                                    {availableRegions.map((region, index) => (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Checkbox
                                                    checked={selectedRegions.includes(region)}
                                                    onChange={isMultiRegion ? () => handleRegionToggle(region) : undefined}
                                                    disabled={!isMultiRegion}
                                                    style={isMultiRegion ? { color: TrimbleColors.Blue } : undefined}
                                                />
                                            }
                                            label={<Typography style={{ fontSize: '18px', fontWeight: 300 }}>{region}</Typography>}
                                        />
                                    ))}
                                    {subscribedRegions.map((region, index) => (
                                        <Tooltip key={`subscribed-${index}`} title="Already subscribed" placement="right">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked
                                                        disabled
                                                    />
                                                }
                                                label={<Typography style={{ fontSize: '18px', fontWeight: 300, color: '#999' }}>{region}</Typography>}
                                            />
                                        </Tooltip>
                                    ))}
                                </FormGroup>
                            </>
                        )}

                        <Typography style={{ marginBottom: 8, marginTop: 50, fontSize: '19px', fontWeight: 400 }}>
                            Description <span style={{ color: 'black', marginLeft: 3 }}>*</span>
                        </Typography>
                        <TextField
                            name="description"
                            onChange={handleFormChange}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Add short description here"
                            variant="outlined"
                            InputProps={{
                                style: {
                                    fontFamily: 'sans-serif',
                                    fontSize: '18px',
                                    fontWeight: 300,
                                },
                            }}
                            inputProps={{
                                style: {
                                    fontFamily: 'sans-serif',
                                    fontSize: '18px',
                                    fontWeight: 300,
                                },
                            }}
                            style={{
                                backgroundColor: '#fff',
                                borderRadius: 4,
                                width: 800,
                                marginTop: 4
                            }}
                        />
                    </Box>
                    <Typography style={{ marginBottom: 5, marginTop: 50, fontSize: '19px', fontWeight: 600 }}>
                        Filter (Optional)
                        <Tooltip title="Use regex pattern for field comparisons.
                                        Operators: && for AND, || for OR.
                                        Example: FieldName1 = Regex1 && FieldName2 = Regex2" placement="right" arrow
                                        classes={{ tooltip: classes.customTooltip }}>
                        <IconButton size="medium" style={{ marginLeft: 8 }}>
                            <InfoOutlinedIcon style={{ color: TrimbleColors.Blue, fontSize: '30px' }} />
                        </IconButton>
                        </Tooltip>
                    </Typography>
                    <Box display="flex" alignItems="center" mb={2} style={{width: 800}}>
                        <TextField
                            name="filter"
                            onChange={handleFormChange}
                            value={filter}
                            fullWidth
                            rows={4}
                            placeholder="Add your Filter here"
                            variant="outlined"
                            InputProps={{
                                style: {
                                    fontFamily: 'sans-serif',
                                    fontSize: '18px',
                                    fontWeight: 300,
                                },
                            }}
                            inputProps={{
                                style: {
                                    fontFamily: 'sans-serif',
                                    fontSize: '18px',
                                    fontWeight: 300,
                                },
                            }}
                            style={{
                                backgroundColor: '#fff',
                                borderRadius: 4,
                                width: 800,
                                marginTop: 4,
                                height: '56px'
                            }}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleVerifyClick}
                            style={{
                                marginLeft: 16,
                                marginTop: 3,
                                border: `2px solid ${TrimbleColors.Blue}`,
                                color: TrimbleColors.Blue,
                                backgroundColor: 'transparent',
                                textTransform: 'none',
                                padding: '12px 24px',
                                fontSize: '20px',
                                minWidth: '120px',
                                height: '56px'
                            }}
                        >
                            Verify
                        </Button>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mt={2} style={{ marginTop: 100, marginRight: 50 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!isFormValid}
                            style={{
                                backgroundColor: TrimbleColors.Blue,
                                color: '#FFFFFF',
                                textTransform: 'none',
                                padding: '30px 32px',
                                fontSize: '20px',
                                minWidth: '180px',
                                opacity: isFormValid ? 1 : 0.5,
                                cursor: isFormValid ? 'pointer' : 'not-allowed',
                                borderRadius: '6px',
                            }}
                        >
                            Add New Configuration
                        </Button>
                    </Box>
                </form>
            </Box>
        </Drawer>
    );
};

export default EventDrawer;
